/*
 * Tile glyphs for the Convert Image to Chart type grid.
 *
 * One shared <svg> wrapper (one viewBox, one stroke weight) so the twelve
 * tiles stay visually even in a grid four across, exactly as chipIcons.tsx
 * does for the action dock. Each glyph is a miniature of the chart it selects
 * — that is what the user reads, not the caption underneath it.
 *
 * Marks use `currentColor`, so a tile's selected/hover state tints its glyph
 * from CSS rather than from a second colour constant here.
 *
 * THESE ARE PLACEHOLDERS on the same footing as chipIcons.tsx: the UI/UX team
 * supplies the final SVGs, and replacing one is a single-line change — swap
 * only the JSX handed to `tileIcon(...)`, keep the export name, and do not
 * fork the wrapper.
 */

import type { ComponentType, ReactNode } from "react";

import type { ChartTypeOptionId } from "../services/chartTypeOptions";

/** Shared wrapper — the single source of tile-glyph sizing/colour. Size comes
 *  from CSS (`.tileIcon svg`), so a replacement inherits it whatever it declares. */
const tileIcon =
  (inner: ReactNode): ComponentType =>
  function ChartTileIcon() {
    return (
      <svg
        viewBox="0 0 24 20"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        data-placeholder-icon="true"
      >
        {inner}
      </svg>
    );
  };

/** Vertical bars of differing heights — Column. */
const ColumnGlyph = tileIcon(
  <>
    <rect x="4" y="5" width="3" height="12" stroke="none" />
    <rect x="10.5" y="5" width="3" height="12" stroke="none" />
    <rect x="17" y="5" width="3" height="12" stroke="none" />
  </>,
);

/** Two bars, each split into a light and a dark segment — Stacked Column. */
const StackedColumnGlyph = tileIcon(
  <>
    <rect x="5" y="9" width="5" height="8" stroke="none" />
    <rect x="5" y="4" width="5" height="4" stroke="none" opacity={0.45} />
    <rect x="14" y="9" width="5" height="8" stroke="none" />
    <rect x="14" y="4" width="5" height="4" stroke="none" opacity={0.45} />
  </>,
);

/** Three horizontal bars — Bar. */
const BarGlyph = tileIcon(
  <>
    <rect x="4" y="4" width="3" height="12" stroke="none" />
    <rect x="10.5" y="4" width="3" height="12" stroke="none" />
    <rect x="17" y="4" width="3" height="12" stroke="none" />
  </>,
);

/** Two wide bars split into segments — Stacked Bar. */
const StackedBarGlyph = tileIcon(
  <>
    <rect x="4" y="4" width="7" height="12" stroke="none" />
    <rect x="12" y="4" width="8" height="12" stroke="none" opacity={0.45} />
  </>,
);

/** Rising polyline — Line. */
const LineGlyph = tileIcon(<path d="M4 15 L10 8 L14 11 L20 4" fill="none" strokeWidth={1.8} />);

/** Circle with one slice cut — Pie. */
const PieGlyph = tileIcon(
  <>
    <circle cx="12" cy="10" r="7" fill="none" strokeWidth={1.6} />
    <path d="M12 10 L12 3 A7 7 0 0 1 19 10 Z" stroke="none" />
  </>,
);

/** Ring — Donut. */
const DonutGlyph = tileIcon(
  <>
    <circle cx="12" cy="10" r="7" fill="none" strokeWidth={1.6} />
    <circle cx="12" cy="10" r="3" fill="none" strokeWidth={1.6} />
  </>,
);

/** Filled triangle rising to the right — Area. */
const AreaGlyph = tileIcon(<path d="M4 16 L10 9 L15 12 L20 5 L20 16 Z" strokeWidth={1.2} />);

/** Two banded areas — Stacked Area. */
const StackedAreaGlyph = tileIcon(
  <>
    <path d="M4 16 L10 11 L15 13 L20 8 L20 16 Z" strokeWidth={1.2} />
    <path d="M4 11 L10 6 L15 8 L20 3 L20 8 L15 13 L10 11 L4 16 Z" strokeWidth={1.2} opacity={0.45} />
  </>,
);

/** Scattered dots — Scatter. */
const ScatterGlyph = tileIcon(
  <>
    <circle cx="6" cy="14" r="1.6" stroke="none" />
    <circle cx="11" cy="8" r="1.6" stroke="none" />
    <circle cx="14" cy="13" r="1.6" stroke="none" />
    <circle cx="19" cy="6" r="1.6" stroke="none" />
  </>,
);

/** Pentagon web — Radar. */
const RadarGlyph = tileIcon(
  <>
    <path d="M12 3 L19 8 L16.5 16 L7.5 16 L5 8 Z" fill="none" strokeWidth={1.4} />
    <path d="M12 7 L15.5 9.5 L14 14 L10 14 L8.5 9.5 Z" strokeWidth={1} opacity={0.5} />
  </>,
);

/** Bars with a line over them — Combo. */
const ComboGlyph = tileIcon(
  <>
    <rect x="5" y="10" width="3.5" height="7" stroke="none" />
    <rect x="14" y="8" width="3.5" height="9" stroke="none" />
    <path d="M4 13 L10 6 L15 9 L20 4" fill="none" strokeWidth={1.6} opacity={0.55} />
  </>,
);

/**
 * Tile id → glyph.
 *
 * Complete by construction: `Record<ChartTypeOptionId, …>` makes a new tile
 * without a glyph a compile error rather than an empty square.
 */
export const CHART_TYPE_ICONS: Readonly<Record<ChartTypeOptionId, ComponentType>> = {
  column: ColumnGlyph,
  stackedColumn: StackedColumnGlyph,
  bar: BarGlyph,
  stackedBar: StackedBarGlyph,
  line: LineGlyph,
  pie: PieGlyph,
  donut: DonutGlyph,
  area: AreaGlyph,
  stackedArea: StackedAreaGlyph,
  scatter: ScatterGlyph,
  radar: RadarGlyph,
  combo: ComboGlyph,
};
